import { useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Download, FileCheck2, Printer } from "lucide-react";
import { parseContract, ReportPreviewMessageSchema } from "@rnavoyager/contracts";
import type { ReportPreviewMessage, ResultDocument } from "@rnavoyager/contracts";
import { renderReportHtml, reportTime, REPORT_DEMO_PREVIEW_STYLE } from "@rnavoyager/domain/report";
import { SessionFileReferencesContext, SessionMarkdown } from "./SessionFileReferences";
import { ReportActionsContext } from "./ReportActions";
import { WorkspaceFilePreviewDialog } from "./WorkspaceFilePreview";
import type { WorkspaceFileEntry } from "./types";
import "./ReportPage.css";

// 游客阅读视图展开章节并禁用离屏跳过；下载和摘要校验始终使用原始已审核 HTML。
function demoPreviewHtml(html: string): string {
  return html
    .replace("</head>", `<style>${REPORT_DEMO_PREVIEW_STYLE}</style></head>`)
    .replaceAll('<details class="report-section"', '<details open class="report-section"');
}

export function ReportPage({
  document,
  sessionId,
  onSelectRow,
  draft,
  publicDemo = false,
}: {
  document: ResultDocument;
  sessionId: string;
  onSelectRow: (blockIndex: number, rowIndex: number, columnIndex?: number) => void;
  draft?: ReactNode;
  publicDemo?: boolean;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const references = useContext(SessionFileReferencesContext);
  const actions = useContext(ReportActionsContext);
  const candidate = actions?.current;
  const current =
    candidate &&
    candidate.reportId === document.report?.reportId &&
    candidate.revision === document.report?.attemptRevision
      ? candidate
      : null;
  const approved =
    document.report?.state === "approved" && (!current || current.status === "approved");
  const html = useMemo(() => (approved ? renderReportHtml(document) : ""), [document, approved]);
  const [retryError, setRetryError] = useState("");
  const [entry, setEntry] = useState<WorkspaceFileEntry | null>(null);
  const [verification, setVerification] = useState<"pending" | "valid" | "invalid">("pending");
  const verified = approved && verification === "valid";
  const metadata = document.report!;
  const retryable = current
    ? ["failed", "cancelled"].includes(current.status)
    : !candidate && metadata.state === "unapproved";
  const canRetryEvidence =
    metadata.failure?.code === "no_progress" && !!metadata.evidenceGaps?.length;
  useEffect(() => {
    let active = true;
    setVerification("pending");
    if (metadata.state === "approved" && metadata.htmlDigest && crypto.subtle)
      void crypto.subtle
        .digest("SHA-256", new TextEncoder().encode(html))
        .then((bytes) => {
          const digest = [...new Uint8Array(bytes)]
            .map((value) => value.toString(16).padStart(2, "0"))
            .join("");
          if (active) setVerification(digest === metadata.htmlDigest ? "valid" : "invalid");
        })
        .catch(() => {
          if (active) setVerification("invalid");
        });
    else setVerification("invalid");
    return () => {
      active = false;
    };
  }, [html, metadata.state, metadata.htmlDigest]);
  useEffect(() => {
    function receive(event: MessageEvent) {
      if (!frame.current?.contentWindow || event.source !== frame.current.contentWindow) return;
      let message: ReportPreviewMessage;
      try {
        message = parseContract(ReportPreviewMessageSchema, event.data);
      } catch {
        return;
      }
      if (message.kind === "report_resize") {
        frame.current.style.height = `${message.height}px`;
        return;
      }
      if (message.kind === "report_selection") {
        const block = document.blocks[message.blockIndex],
          row = message.rowIndex,
          column = message.columnIndex;
        const valid =
          block?.kind === "table"
            ? !!block.rows[row] && (column === undefined || column < block.columns.length)
            : block?.kind === "sequence"
              ? !!block.items[row] && column === undefined
              : block?.kind === "chart"
                ? row < block.series.reduce((sum, series) => sum + series.points.length, 0) &&
                  column === undefined
                : block?.kind === "matrix"
                  ? column !== undefined && block.rows[row]?.values[column] !== undefined
                  : false;
        if (valid) onSelectRow(message.blockIndex, row, column);
        return;
      }
      const artifact = document.blocks
        .flatMap((block) => (block.kind === "artifacts" ? block.items : []))
        .find((item) => item.artifactId === message.artifactId);
      if (!artifact) return;
      if (references) references.openFile(artifact.path);
      else
        setEntry({
          path: `/project/${artifact.path}`,
          name: artifact.name,
          kind: "file",
          size: artifact.sizeBytes,
          media_type: artifact.mediaType,
          previewable: true,
        });
    }
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [document, onSelectRow, references]);

  function download() {
    if (!verified) return;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
    const link = window.document.createElement("a");
    link.href = url;
    link.download = `RNAVoyager-报告-${document.documentId}.html`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  const stateLabel =
    metadata.failure?.code === "evidence_missing"
      ? "报告待补充研究证据"
      : metadata.failure?.code === "capability_unavailable"
        ? "报告缺少可用的补证能力"
        : canRetryEvidence
          ? "补证未取得新证据"
          : current?.status === "stale"
            ? "报告已过期 · 研究目标已更新"
            : current?.status === "cancelled"
              ? "报告已取消 · 计算结果保留"
              : metadata.state === "approved"
                ? verified
                  ? "报告审核通过"
                  : verification === "pending"
                    ? "正在核验报告修订"
                    : "草稿 · 当前修订未通过审核"
                : metadata.state === "reviewing"
                  ? "正在审核报告"
                  : metadata.state === "revising"
                    ? "正在修订报告"
                    : metadata.state === "unapproved"
                      ? "草稿 · 审核未通过"
                      : "报告草稿";
  return (
    <div className="report-page">
      <div className="report-page-toolbar">
        <span role="status">
          <FileCheck2 aria-hidden size={16} />
          {stateLabel}
        </span>
        {verified && (
          <button type="button" onClick={download}>
            <Download aria-hidden size={15} />
            下载 HTML
          </button>
        )}
        {verified && (
          <button
            type="button"
            onClick={() => {
              if (frame.current?.contentWindow)
                frame.current.contentWindow.postMessage({ kind: "report_print" }, "*");
            }}
          >
            <Printer aria-hidden size={15} />
            打印
          </button>
        )}
        {actions &&
          actions.currentReportId === metadata.reportId &&
          metadata.attemptRevision &&
          metadata.sourceDigest &&
          retryable &&
          (metadata.failure?.retryable !== false || canRetryEvidence) && (
            <button
              type="button"
              disabled={actions.busy}
              onClick={() => {
                setRetryError("");
                void actions
                  .retry({
                    revision: metadata.attemptRevision!,
                    evidenceDigest: metadata.sourceDigest!,
                  })
                  .catch((error: unknown) =>
                    setRetryError(error instanceof Error ? error.message : "报告重试未接纳"),
                  );
              }}
            >
              {canRetryEvidence ? "补证并重试" : "重试报告"}
            </button>
          )}
      </div>
      {retryError && <p role="alert">{retryError}</p>}
      {metadata.approvedAt && verified && !publicDemo && (
        <p className="report-audit-time">审核通过于 {reportTime(metadata.approvedAt)}</p>
      )}
      {metadata.issues.length > 0 && (
        <details className="report-audit-issues" open={metadata.state === "unapproved"}>
          <summary>报告检查意见（{metadata.issues.length}）</summary>
          <ul>
            {metadata.issues.map((issue, index) => (
              <li key={index}>
                {issue.message}
                {issue.remedy && <p>{issue.remedy}</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
      {verified ? (
        <iframe
          ref={frame}
          title={`${document.title} · RNAVoyager 报告`}
          sandbox="allow-scripts allow-modals allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          srcDoc={publicDemo ? demoPreviewHtml(html) : html}
          className="report-page-frame"
        />
      ) : (
        <div className="report-markdown-draft" aria-label="报告 Markdown 草稿">
          {draft ??
            document.blocks
              .filter((block) => block.kind === "markdown")
              .map((block, index) => (
                <SessionMarkdown key={index} sessionId={sessionId}>
                  {block.markdown}
                </SessionMarkdown>
              ))}
        </div>
      )}
      {entry && (
        <WorkspaceFilePreviewDialog
          entry={entry}
          sessionId={sessionId}
          onClose={() => setEntry(null)}
        />
      )}
    </div>
  );
}
